import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  computed,
  input,
  signal,
  viewChild
} from '@angular/core';
import { InputColor, InputSize, RadioSelection } from '../inputs.models';
import { filter, fromEvent, map, of, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RADIO_HOST } from '../inputs.constants';

@Component({
  selector: 'radio',
  standalone: true,
  imports: [],
  host: RADIO_HOST,
  template: `<label
    [attr.for]="id() ? id() : null"
    class="input-radio"
    [class]="_classList()"
    tabindex="0"
    (blur)="emitFocusChange()"
    (keyup)="handleKeyupEvent($event)"
  >
    <input
      #nativeRadio
      type="radio"
      class="input-radio__native-input"
      [attr.id]="id() ? id() : null"
      [attr.checked]="isChecked() ? 'true' : null"
      [attr.disabled]="isDisabled() ? 'true' : null"
      [attr.readonly]="isReadonly() ? 'true' : null"
      [attr.name]="inputName() ? inputName() : null"
      [value]="value()"
      tabindex="-1"
    />
    <span class="input-radio__indicator-wrapper">
      <span class="input-radio__indicator"></span>
      <span class="input-radio__indicator-background"></span>
    </span>
    <span class="input-radio__label"><ng-content></ng-content></span>
  </label>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RadioButtonComponent implements AfterViewInit, OnChanges {
  @Output() selectionChange = new EventEmitter<RadioSelection>();
  @Output() focusChange = new EventEmitter<void>();
  uniqueId = input.required<string>();
  value = input.required<string>();
  checked = input(false);
  disabled = input(false);
  readonly = input(false);
  labelledby = input<string | undefined | null>(null);

  nativeRadioChild = viewChild<ElementRef<HTMLInputElement>>('nativeRadio');

  id = signal('');
  isChecked = signal(false);
  isDisabled = signal(false);
  isReadonly = signal(false);
  color = signal<InputColor>('secondary');
  size = signal<InputSize>('md');
  inputName = signal('');

  protected _classList = computed(() => {
    return `input-radio--${this.size()} input-radio--${this.color()} ${this.isChecked() ? 'input-radio--checked' : ''} ${this.isDisabled() ? 'input-radio--disabled' : ''} ${this.readonly() ? 'input-radio--readonly' : ''}`;
  });

  protected _labelledby = computed(() => this.labelledby());

  private get _nativeRadio(): HTMLInputElement | undefined {
    return this.nativeRadioChild()?.nativeElement;
  }

  get destroyRef(): DestroyRef {
    return this._destroyRef;
  }

  constructor(
    private _destroyRef: DestroyRef,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('checked' in changes) {
      this.isChecked.set(this.checked());
    }

    if ('disabled' in changes) {
      this.isDisabled.set(this.disabled());
    }

    if ('readonly' in changes) {
      this.isReadonly.set(this.readonly());
    }

    if ('uniqueId' in changes) {
      if (!this.uniqueId().length || this.uniqueId().includes(' ')) {
        throw new Error('uniqueId must not be empty or contain spaces.');
      } else {
        this.id.set(this.uniqueId());
      }
    }
  }

  ngAfterViewInit(): void {
    this._handleNativeInputCheckedStatusChanges();
  }

  private _handleNativeInputCheckedStatusChanges(): void {
    if (!(this._nativeRadio && this.id() && !this.isReadonly())) return;

    fromEvent(this._nativeRadio, 'change')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(e => e.target as HTMLInputElement | null),
        filter(Boolean),
        switchMap(element => {
          this.isChecked.set(element.checked);
          this.selectionChange.emit({
            id: this.id(),
            value: this.value()
          });
          return of(this.isChecked());
        })
      )
      .subscribe();
  }

  detectChange(): void {
    this._cdr.detectChanges();
  }

  emitFocusChange(): void {
    this.focusChange.emit();
  }

  handleKeyupEvent(event: KeyboardEvent): void {
    if (event.code === 'Space' || event.code === 'Enter') {
      this._nativeRadio?.click();
    }
  }
}
